import { motion, useInView } from "framer-motion";
import {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo, // MODIFIED: Added useMemo
} from "react";
import { NavLink } from "react-router-dom";
import "./PhotoGallery.css"; // Ensure this path is correct

// STEP 1: Define your simple, clean image data.
// This is now the single source of truth for your gallery content.
const imageData = [
  {
    src: "assets/images/gallery/year-2025/Registration/Registration-1.jpg",
    description: "Guests arriving and registering for the event.",
  },
  {
    src: "assets/images/gallery/year-2025/Speaker/Speaker-13.jpg",
    description: "Panel discussion with industry experts.",
  },
  {
    src: "assets/images/gallery/year-2025/Speaker/Speaker-7.jpg",
    description: "A speaker interacting with the audience.",
  },
  {
    src: "assets/images/gallery/year-2025/Speaker/Speaker-4.jpg",
    description: "An engaging talk from one of our keynote speakers.",
  },
  {
    src: "assets/images/gallery/year-2025/Networking/Networking-23.jpg",
    description: "Collaborative discussions and idea sharing.",
  },
  {
    src: "assets/images/gallery/year-2025/Networking/Networking-4.jpg",
    description: "Building connections during the networking session.",
  },  
  {
    src: "assets/images/gallery/year-2025/Networking/Networking-6.jpg",
    description: "Attendees networking during a coffee break.",
  },
  {
    src: "assets/images/gallery/year-2025/Speaker/Speaker-14.jpg",
    description: "Closing remarks and a thank you to all participants.",
  },
];

const PhotoGallery = () => {
  const mainPhotoRef = useRef(null);
  const isMainPhotoInView = useInView(mainPhotoRef, { once: true });

  // STEP 2: Declare all hooks at the top level, as required by React.
  // We will map these to our imageData array in the next step.
  const allRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
  ];

  const allInViews = [
    useInView(allRefs[0], { once: true }),
    useInView(allRefs[1], { once: true }),
    useInView(allRefs[2], { once: true }),
    useInView(allRefs[3], { once: true }),
    useInView(allRefs[4], { once: true }),
    useInView(allRefs[5], { once: true }),
    useInView(allRefs[6], { once: true }),
    useInView(allRefs[7], { once: true }),
  ];

  // STEP 3: Use useMemo to combine the data and the hook results.
  // This list is now fully "hydrated" with everything the component needs.
  // useMemo ensures this only recalculates when an image's view state changes.
  const imageList = useMemo(
    () =>
      imageData.map((image, index) => ({
        ...image, // spread in the src and description
        id: `photo${index + 1}`, // dynamically create an ID
        ref: allRefs[index], // assign the correct ref
        currentInView: allInViews[index], // assign the correct inView state
      })),
    [allInViews] // Dependency array: only re-run if an inView state changes
  );

  const [animationsPlayed, setAnimationsPlayed] = useState(() => {
    const savedState = sessionStorage.getItem("photoGalleryAnimationsPlayed");
    return savedState ? JSON.parse(savedState) : {};
  });

  // This useEffect now depends on the memoized `imageList`, making it more efficient.
  useEffect(() => {
    const newAnimations = {};
    let updated = false;
    imageList.forEach((item) => {
      if (item.currentInView && !animationsPlayed[item.id]) {
        newAnimations[item.id] = true;
        updated = true;
      }
    });

    if (updated) {
      const updatedState = { ...animationsPlayed, ...newAnimations };
      setAnimationsPlayed(updatedState);
      sessionStorage.setItem(
        "photoGalleryAnimationsPlayed",
        JSON.stringify(updatedState)
      );
    }
  }, [animationsPlayed, imageList]);

  const [modalOpen, setModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openModal = (index) => {
    setCurrentIndex(index);
    setModalOpen(true);
  };

  const closeModal = useCallback(() => setModalOpen(false), []);

  const showNext = useCallback(
    () => setCurrentIndex((prev) => (prev + 1) % imageList.length),
    [imageList.length]
  );

  const showPrev = useCallback(
    () =>
      setCurrentIndex(
        (prev) => (prev - 1 + imageList.length) % imageList.length
      ),
    [imageList.length]
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") showNext();
      else if (e.key === "ArrowLeft") showPrev();
      else if (e.key === "Escape") closeModal();
    };

    if (modalOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [modalOpen, showNext, showPrev, closeModal]);

  const photoContainerVariants = {
    visible: { transition: { staggerChildren: 0.2 } },
  };

  const badgephotoItem = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const headingVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <section
      className="service_section section_space section_decoration"
      style={{ overflowX: "hidden" }}
    >
      <div className="container">
        <div className="heading_block text-center">
          <motion.div
            className="badge style_2 text-uppercase"
            ref={mainPhotoRef}
            variants={photoContainerVariants}
            initial="hidden"
            animate={isMainPhotoInView ? "visible" : "hidden"}
          >
            <motion.span variants={badgephotoItem}>
              <i className="fa-solid fa-circle text-primary"></i> Our Gallery{" "}
              <i className="fa-solid fa-circle text-primary"></i>
            </motion.span>
          </motion.div>
          <motion.h2
            ref={mainPhotoRef}
            variants={headingVariants}
            initial="hidden"
            animate={isMainPhotoInView ? "visible" : "hidden"}
            className="heading_text mb-0"
          >
            Photo Gallery
          </motion.h2>
        </div>

        {/* The rest of the component remains the same, as it maps over the generated imageList */}
        <div className="row align-items-center mt-5">
          {imageList.map((item, index) => (
            <motion.div
              key={item.id}
              className="col-lg-3 col-md-6 col-sm-6 col-6 d-flex justify-content-center"
              initial={{ opacity: 0, x: 100 }}
              animate={
                item.currentInView || animationsPlayed[item.id]
                  ? { opacity: 1, x: 0 }
                  : { opacity: 0, x: 100 }
              }
              transition={{ duration: 0.6, delay: index * 0.2 }}
              ref={item.ref}
            >
              <img
                src={item.src}
                alt={`Gallery ${index + 1}`}
                className="img-fluid gallery-thumb mb-4"
                onClick={() => openModal(index)}
              />
            </motion.div>
          ))}
        </div>

        <motion.div
          className="col-lg-12 mt-5"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <NavLink className="btn bg-primary text-dark" to="/photos2025">
            <span className="btn_label">Show All Photos</span>
          </NavLink>
        </motion.div>
      </div>

      {modalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <img
              src={imageList[currentIndex].src}
              alt={`Gallery ${currentIndex + 1}`}
              className="modal-image"
            />
            {imageList[currentIndex].description && (
              <p className="modal-description">
                {imageList[currentIndex].description}
              </p>
            )}
            <button className="modal-close" onClick={closeModal}>
              ×
            </button>
            <button className="modal-prev" onClick={showPrev}>
              ❮
            </button>
            <button className="modal-next" onClick={showNext}>
              ❯
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default PhotoGallery;